import { type FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';

import { Card, Elevation, Spinner } from '@blueprintjs/core';

import DialogBox from 'components/Dialog/BasicDialog/DialogBox';
import { alertToast } from 'components/Toast/AlertToast';
import i18n from 'i18n/i18n';
import { type AbstractRuleSet, type DunningSelectionConfiguration, RIGHTS } from 'interfaces/Interfaces';
import { Button } from 'lib/Button';
import {
  getAuditForSelection,
  getBasicRuleSets,
  getDunningSelection,
  getRuleSet,
  updateDunningSelection,
} from 'services/ApiService';
import { testAttribute } from 'util/Util';

import DunningSelection from './DunningSelection/DunningSelection';
import { UserContext } from '../../App';
import { AuditEntryDto } from '../../generated-sources/openapi';
import { useCallbackPrompt } from '../../hooks/UseCallbackPrompt';
import AuditList from '../Audit/AuditList';

import './DunningSelection.style.sass';

const AUDIT_PAGE_SIZE = 10;

const DunningSelectionPage: FunctionComponent = () => {
  const [ruleSets, setRuleSets] = useState<AbstractRuleSet[]>([]);
  const [dunningSelection, setDunningSelection] = useState<DunningSelectionConfiguration>();
  const [dunningSelectionIsValid, setDunningSelectionIsValid] = useState(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showDialog);
  const userContext = useContext(UserContext);
  const [rulesetNameById, setRulesetNameById] = useState<Map<string, string>>(new Map());
  const [ruleNameById, setRuleNameById] = useState<Map<string, string>>(new Map());
  const [auditRows, setAuditRows] = useState<AuditEntryDto[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(0);
  const [auditHasMore, setAuditHasMore] = useState(true);

  function mapCsvToNames(csv: string, dict: Map<string, string>): string {
    if (!csv) return '';
    return csv
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0)
      .map(id => dict.get(id) ?? id)
      .join(', ');
  }

  function enrichAuditWithNames(
    rows: AuditEntryDto[],
    rulesets: Map<string, string>,
    rules: Map<string, string>,
  ): AuditEntryDto[] {
    return rows.map(r => {
      let oldDisplay = r.oldValueDisplay ?? r.oldValue ?? '';
      let newDisplay = r.newValueDisplay ?? r.newValue ?? '';

      if (r.dictionary === 'rulesets') {
        oldDisplay = mapCsvToNames(r.oldValue ?? '', rulesets);
        newDisplay = mapCsvToNames(r.newValue ?? '', rulesets);
      } else if (r.dictionary === 'rules') {
        oldDisplay = mapCsvToNames(r.oldValue ?? '', rules);
        newDisplay = mapCsvToNames(r.newValue ?? '', rules);
      }
      return {
        ...r,
        oldValueDisplay: oldDisplay,
        newValueDisplay: newDisplay,
      };
    });
  }

  const displayAuditRows = useMemo(
    () => enrichAuditWithNames(auditRows, rulesetNameById, ruleNameById),
    [auditRows, rulesetNameById, ruleNameById],
  );

  function loadAudit(page = 0, append = false) {
    setAuditLoading(true);
    getAuditForSelection(page, AUDIT_PAGE_SIZE)
      .then(rows => {
        const list = rows ?? [];
        setAuditRows(prev => (append ? [...prev, ...list] : list));
        setAuditPage(page);
        setAuditHasMore(list.length === AUDIT_PAGE_SIZE);
      })
      .catch(alertToast)
      .finally(() => setAuditLoading(false));
  }

  async function loadRuleDictionaries() {
    const rs = await getBasicRuleSets();
    setRuleSets(rs);
    const rulesetMap = new Map<string, string>();
    rs.forEach(r => rulesetMap.set(r.rulesetId, r.name));
    setRulesetNameById(rulesetMap);
    const fullRuleSets = await Promise.all(rs.map(r => getRuleSet(r.rulesetId)));
    const ruleMap = new Map<string, string>();
    fullRuleSets.forEach(fr => {
      (fr.rules ?? []).forEach(rule => {
        ruleMap.set(rule.id, rule.name);
      });
    });
    setRuleNameById(ruleMap);
  }

  useEffect(() => {
    loadRuleDictionaries().catch(alertToast);
    getDunningSelection()
      .then(selection => {
        setDunningSelection(selection);
        loadAudit(0, false);
      })
      .catch(alertToast);
  }, []);

  function submitDunningSelection() {
    if (!dunningSelection) return;
    updateDunningSelection(dunningSelection)
      .then(() => {
        setDunningSelectionIsValid(false);
        setShowDialog(false);
        loadAudit(0, false);
      })
      .catch(e => alertToast(e));
  }

  const handleChange = (selection: DunningSelectionConfiguration) => {
    setShowDialog(true);
    setDunningSelection(selection);
  };

  if (!dunningSelection)
    return (
      <div className="dunning-selection">
        <Spinner />
      </div>
    );
  const hasUpdateDSRights = userContext.rights.includes(RIGHTS.UPDATE_DUNNING_SELECTION);
  return (
    <div className="dunning-selection">
      <DialogBox showDialog={showPrompt} confirmNavigation={confirmNavigation} cancelNavigation={cancelNavigation} />
      <div className="title">
        <div className="flex-between">
          {i18n.t('dunning-selection.title')}
          {hasUpdateDSRights && (
            <Button
              testId={testAttribute('4a4a', 'dunning-selection.save')}
              onClick={() => {
                submitDunningSelection();
              }}
              disabled={!dunningSelectionIsValid}>
              {i18n.t('dunning-selection.save')}
            </Button>
          )}
        </div>
      </div>
      <Card className="selection-card" interactive={false} elevation={Elevation.ONE}>
        <DunningSelection
          ruleSets={ruleSets}
          dunningSelection={dunningSelection}
          onSelectionChange={selection => {
            if (hasUpdateDSRights) {
              handleChange(selection);
            }
          }}
          onValidChange={isValid => {
            setDunningSelectionIsValid(isValid);
          }}
        />
      </Card>
      <AuditList
        className="ds-audit-card"
        rows={displayAuditRows}
        loading={auditLoading}
        hasMore={auditHasMore}
        onReachBottom={() => {
          if (!auditLoading && auditHasMore) {
            loadAudit(auditPage + 1, true);
          }
        }}
      />
    </div>
  );
};

export { DunningSelectionPage };
